import React, {useCallback, useEffect, useMemo, useState} from "react";
import { ParamKeyValuePair, useSearchParams } from "react-router-dom";
import {Col, Row, Form} from "react-bootstrap";
import {SortDown, SortUp} from "react-bootstrap-icons";
import {
    ADMIN_LANG,
    AVAILABLE_LANGUAGES,
} from "../../../settings";
import AsyncSelect from "react-select/async";
import {PlainDetails} from "../../../types/admin";
import {useAppDispatch, useAppSelector} from "../../../app/store";
import {getPlainGames, getPlainTeams} from "../../../app/slices/admin";


export const useSearchParamHandler = (paramKey: string) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const handleParamChange = useCallback(
        (value: string) => {
            let entries: ParamKeyValuePair[] = [];
            searchParams.forEach((val, key) => {
                if (key !== paramKey) {
                    entries.push([key, val]);
                }
            });
            entries.push([paramKey, value]);
            setSearchParams(entries);
        },
        [searchParams, setSearchParams, paramKey]
    );

    const currentValue = searchParams.get(paramKey) || '';

    return { currentValue, handleParamChange };
};

export const PassedFilter = React.memo(() => {
    const { currentValue: passed, handleParamChange: handleChange } = useSearchParamHandler('passed');

    return (
        <Col xs={6} md={4} lg={2}>
            <Form.Group controlId='passed'>
                <Form.Label>{ADMIN_LANG.passed}</Form.Label>
                <Form.Select value={passed} onChange={(e) => handleChange(e.target.value)}>
                    <option value=''>{ADMIN_LANG.all}</option>
                    <option value='true'>{ADMIN_LANG.yes}</option>
                    <option value='false'>{ADMIN_LANG.no}</option>
                </Form.Select>
            </Form.Group>
        </Col>
    );
});

export const IsActiveFilter = React.memo(() => {
    const { currentValue: is_active, handleParamChange: handleChange } = useSearchParamHandler('is_active');

    return (
        <Col xs={6} md={4} lg={2}>
            <Form.Group controlId='filter_is_active'>
                <Form.Label>{ADMIN_LANG.is_active}</Form.Label>
                <Form.Select value={is_active} onChange={(e) => handleChange(e.target.value)}>
                    <option value=''>{ADMIN_LANG.all}</option>
                    <option value='true'>{ADMIN_LANG.yes}</option>
                    <option value='false'>{ADMIN_LANG.no}</option>
                </Form.Select>
            </Form.Group>
        </Col>
    );

});
export const IsStaffFilter = React.memo(() => {
    const { currentValue: is_staff, handleParamChange: handleChange } = useSearchParamHandler('is_staff');

    return (
        <Col xs={6} md={4} lg={2}>
            <Form.Group controlId='filter_is_staff'>
                <Form.Label>{ADMIN_LANG.is_staff}</Form.Label>
                <Form.Select value={is_staff} onChange={(e) => handleChange(e.target.value)}>
                    <option value=''>{ADMIN_LANG.all}</option>
                    <option value='true'>{ADMIN_LANG.yes}</option>
                    <option value='false'>{ADMIN_LANG.no}</option>
                </Form.Select>
            </Form.Group>
        </Col>
    );

});
export const IsSuperuserFilter = React.memo(() => {
    const { currentValue: is_superuser, handleParamChange: handleChange } = useSearchParamHandler('is_superuser');

    return (
        <Col xs={6} md={4} lg={2}>
            <Form.Group controlId='filter_is_superuser'>
                <Form.Label>{ADMIN_LANG.is_superuser}</Form.Label>
                <Form.Select value={is_superuser} onChange={(e) => handleChange(e.target.value)}>
                    <option value=''>{ADMIN_LANG.all}</option>
                    <option value='true'>{ADMIN_LANG.yes}</option>
                    <option value='false'>{ADMIN_LANG.no}</option>
                </Form.Select>
            </Form.Group>
        </Col>
    );

});

export const GameFilter = React.memo(() => {
    const { games, game_plain } = useAppSelector((state) => state.admin);
    const { currentValue: game, handleParamChange: handleGameChange } = useSearchParamHandler('game');
    const dispatch = useAppDispatch();
    const [selectedGame, setSelectedGame] = useState<PlainDetails | null>(null);
    const [debounceTimeout, setDebounceTimeout] = useState<number | null>(null);

    // Effect to set the selected game based on the current game_plain if available
    useEffect(() => {
        if (!game) {
            setSelectedGame(null);
        } else if (game && !selectedGame && game_plain) {
            setSelectedGame(game_plain);
        }
    }, [game, selectedGame, game_plain]);

    // Debounced loadOptions function using setTimeout/clearTimeout
    const loadOptions = useCallback((inputValue: string) => {
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }

        return new Promise<PlainDetails[]>((resolve) => {
            const timeout = setTimeout(async () => {
                if (inputValue.length < 2) {
                    resolve([]); // Return empty if input length is less than 2
                    return;
                }

                try {
                    const fulfilledAction = await dispatch(getPlainGames(inputValue));
                    const data: PlainDetails[] = fulfilledAction.payload.games;
                    resolve(data);
                } catch {
                    resolve([]);
                }
            }, 500); // Delay of 500ms for debouncing

            setDebounceTimeout(timeout as unknown as number);
        });
    }, [dispatch, debounceTimeout]);

    // Memoize games to avoid re-renders when games state changes unnecessarily
    const memoizedGames = useMemo(() => games, [games]);

    // Handle selection changes
    const onSelect = useCallback((selectedValue: PlainDetails | null) => {
        if (!selectedValue) {
            handleGameChange('');
            setSelectedGame(null);
        } else {
            setSelectedGame(selectedValue);
            handleGameChange(selectedValue.value);
        }
    }, [handleGameChange]);

    // Cleanup timeout on unmount to avoid memory leaks
    useEffect(() => {
        return () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
        };
    }, [debounceTimeout]);

    return (
        <Col xs={6} md={4} lg={3}>
            <Form.Group controlId="filter_game">
                <Form.Label>{ADMIN_LANG.game}</Form.Label>
                <AsyncSelect
                    value={selectedGame}
                    cacheOptions
                    defaultOptions={memoizedGames}
                    loadOptions={loadOptions}
                    loadingMessage={() => ADMIN_LANG.loading}
                    noOptionsMessage={() => ADMIN_LANG.search_games}
                    classNamePrefix="react-select"
                    className="custom-react-select"
                    onChange={(selectedValue) => onSelect(selectedValue as PlainDetails)}
                    placeholder={ADMIN_LANG.search}
                    isClearable
                />
            </Form.Group>
        </Col>
    );
});

export const TeamFilter = React.memo(() => {
    const { teams, team_plain } = useAppSelector((state) => state.admin);
    const { currentValue: team, handleParamChange: handleTeamChange } = useSearchParamHandler('team');
    const dispatch = useAppDispatch();
    const [selectedTeam, setSelectedTeam] = useState<PlainDetails | null>(null);
    const [debounceTimeout, setDebounceTimeout] = useState<number | null>(null);

    // Effect to reset selected team if no team is set in the state or update it when team_plain is available
    useEffect(() => {
        if (!team) {
            setSelectedTeam(null);
            return;
        }

        if (team && !selectedTeam && team_plain) {
            setSelectedTeam(team_plain);
        }
    }, [team, selectedTeam, team_plain]);

    // Debounced loadOptions function using setTimeout/clearTimeout
    const loadOptions = useCallback((inputValue: string) => {
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }

        return new Promise<PlainDetails[]>((resolve) => {
            const timeout = setTimeout(async () => {
                if (inputValue.length < 2) {
                    resolve([]); // Return empty if the input length is less than 2
                    return;
                }

                try {
                    const fulfilledAction = await dispatch(getPlainTeams(inputValue));
                    const data: PlainDetails[] = fulfilledAction.payload.teams;
                    resolve(data);
                } catch {
                    resolve([]);
                }
            }, 500); // Delay of 500ms for debouncing

            setDebounceTimeout(timeout as unknown as number);
        });
    }, [dispatch, debounceTimeout]);

    // Handle selection
    const onSelect = useCallback((selectedValue: PlainDetails | null) => {
        if (!selectedValue) {
            handleTeamChange('');
            setSelectedTeam(null);
        } else {
            setSelectedTeam(selectedValue);
            handleTeamChange(selectedValue.value);
        }
    }, [handleTeamChange]);

    // Cleanup timeout on unmount to avoid memory leaks
    useEffect(() => {
        return () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
        };
    }, [debounceTimeout]);

    return (
        <Col xs={6} md={4} lg={3}>
            <Form.Group controlId="filter_team">
                <Form.Label>{ADMIN_LANG.team}</Form.Label>
                <AsyncSelect
                    value={selectedTeam}
                    cacheOptions
                    defaultOptions={teams}
                    loadOptions={loadOptions}
                    noOptionsMessage={() => ADMIN_LANG.search_teams}
                    classNamePrefix="react-select"
                    className="custom-react-select"
                    onChange={(selectedValue) => onSelect(selectedValue as PlainDetails)}
                    placeholder={ADMIN_LANG.search}
                    isClearable
                />
            </Form.Group>
        </Col>
    );
});

export const TypeFilter = React.memo(() => {
    const {currentValue: type, handleParamChange: handleTypeChange} = useSearchParamHandler('type');

    return (
        <Col xs={6} md={4} lg={3}>
            <Form.Group controlId='filter_type'>
                <Form.Label>{ADMIN_LANG.type}</Form.Label>
                <Form.Select value={type} onChange={(e) => handleTypeChange(e.target.value)}>
                    <option value=''>{ADMIN_LANG.all}</option>
                    <option value='select'>{ADMIN_LANG.type_select}</option>
                    <option value='select_multiple'>{ADMIN_LANG.type_select_multiple}</option>
                    <option value='order'>{ADMIN_LANG.type_order}</option>
                    <option value='connect'>{ADMIN_LANG.type_connect}</option>
                    <option value='text'>{ADMIN_LANG.type_text}</option>
                </Form.Select>
            </Form.Group>
        </Col>
    );
});

export const StatusFilter = React.memo(() => {
    const {currentValue: status, handleParamChange: handleStatusChange} = useSearchParamHandler('status');

    return (
        <Col xs={6} md={4} lg={3}>
            <Form.Group controlId='filter_status'>
                <Form.Label>{ADMIN_LANG.playable}</Form.Label>
                <Form.Select value={status} onChange={(e) => handleStatusChange(e.target.value)}>
                    <option value=''>{ADMIN_LANG.all}</option>
                    <option value='1'>{ADMIN_LANG.yes}</option>
                    <option value='0'>{ADMIN_LANG.no}</option>
                </Form.Select>
            </Form.Group>
        </Col>
    );
});

export const LanguageFilter = React.memo(() => {
    const { currentValue: language, handleParamChange: handleLanguageChange } = useSearchParamHandler('language');

    return (
        <Col xs={6} md={4} lg={3}>
            <Form.Group controlId='filter_language'>
                <Form.Label>{ADMIN_LANG.language}</Form.Label>
                <Form.Select value={language} onChange={(e) => handleLanguageChange(e.target.value)}>
                    <option value=''>{ADMIN_LANG.all}</option>
                    {AVAILABLE_LANGUAGES.map((lang) => (
                        <option key={lang} value={lang}>{lang}</option>
                    ))}
                </Form.Select>
            </Form.Group>
        </Col>
    );
});

type SortableHeaderProps = {
    label: string;
    sortValue: string;
};

export const SortableHeader = React.memo(({ label, sortValue }: SortableHeaderProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentSortValue = searchParams.get('sort_value');
    const currentSortDirection = searchParams.get('sort_direction') || 'asc';

    const handleSort = useCallback(() => {
        let entries: ParamKeyValuePair[] = [];

        searchParams.forEach((value, key) => {
            if (key !== 'sort_value' && key !== 'sort_direction') {
                entries.push([key, value]);
            }
        });

        let newSortDirection = 'asc';
        let newSortValue = sortValue;

        if (currentSortValue === sortValue) {
            if (currentSortDirection === 'asc') {
                newSortDirection = 'desc';
            } else if (currentSortDirection === 'desc') {
                newSortValue = '';
            }
        }

        if (newSortValue) {
            entries.push(['sort_value', newSortValue]);
            entries.push(['sort_direction', newSortDirection]);
        }

        setSearchParams(entries);
    }, [currentSortValue, currentSortDirection, searchParams, setSearchParams, sortValue]);

    const renderSortIcon = () => {
        if (currentSortValue !== sortValue) return null;
        return currentSortDirection === 'asc' ? <SortUp /> : <SortDown />;
    };

    return (
        <th key={sortValue} onClick={handleSort} className='sortable-header'>
            <span className='sortable-label'>{label}</span>
            <span className='sortable-icon'>{renderSortIcon()}</span>
        </th>
    );
});

const filterComponentMap: { [key: string]: React.ComponentType } = {
    language: LanguageFilter,
    status: StatusFilter,
    type: TypeFilter,
    game: GameFilter,
    team: TeamFilter,
    is_active: IsActiveFilter,
    is_staff: IsStaffFilter,
    is_superuser: IsSuperuserFilter,
};

type FilterRendererProps = {
    filters: string[];
};

export const FilterRender = React.memo(({ filters }: FilterRendererProps) => {
    return (
        <Row className="gy-3">
            {filters.map((filter) => {
                const FilterComponent = filterComponentMap[filter];
                return FilterComponent ? <FilterComponent key={filter} /> : null;
            })}
        </Row>
    );
});